// client/src/features/quotations/pendingCataloguePickVat.ts
//
// What a PENDING catalogue pick's price becomes once "Does this item have
// VAT?" (`CatalogueVatPrompt.tsx`) is answered — a pick from the item-lookup
// panel whose row's `vat_applicable` was blank, asked BEFORE the line is
// written (`QuotationDetailPage.handleSearchSelect`). Split out,
// DEPENDENCY-INJECTED for its one network call, so it can be unit-tested
// without rendering `QuotationDetailPage` (a god file under a line ceiling,
// TECH_DEBT #26) — the same shape `pendingSupplierVatPick.ts` uses for the
// OTHER same-session pick.
//
// ── THE FIRST BUG THIS CLOSED ────────────────────────────────────────────
// The popup's item-lookup panel offers a catalogue row at any unit it can
// honestly state a price for (`costUnitOptions` → `priceSelection`, e.g.
// "Per Pack" or "Per kg" off a case row) — that choice is
// `priceSelection.unitKey`. The same-session VAT re-price used to run from
// the row's BASE-unit cost (`catalogueRowCostPrice`) and just keep whatever
// unit the line already carried, so a user who had picked "Per Pack"
// answered Yes/No and got the CASE price under the pack unit instead.
//
// The fix: re-derive the SAME `priceSelection.unitKey` for the ANSWERED row
// — through `priceAtUnit`, the exact owner the lookup panel itself used to
// build `priceSelection` in the first place (`itemLookupRows.ts`) — never a
// second pricing formula.
//
// ── THE SECOND BUG THIS CLOSES (audit finding, 2026-09-25) ───────────────
// `priceAtUnit` prices a catalogue row by reading ITS OWN `priceLevels`
// (`costPriceTiersTtd`'s stored-tier branch: `storedCaseTtd`, `ttPerLevel1`,
// `priceLevels.byUnit[].ttAmount`) before falling back to a derivation from
// `item_price`. Those stored tiers are computed SERVER-SIDE, at the moment
// the row was last written — and for a row whose VAT was blank, that moment
// was BEFORE this popup ever asked, so they are the NO-VAT figures. Setting
// `answeredItem.vat_applicable = true` on the in-memory `item` changes
// nothing about those already-computed numbers: `storedCaseTtd` reads
// `priceLevels.ttPerLevel2` verbatim and never re-grosses it. A blank-VAT
// row answered "Yes" was landing back on its OWN pre-answer, VAT-EXCLUSIVE
// stored price — TT$55.44/kg stayed TT$55.44/kg instead of becoming the
// TT$62.37 USC actually pays.
//
// The fix: once `setCatalogueVat` has saved the answer, RE-FETCH the row
// (`deps.getCatalogueItem` — the same typed single-item GET
// `SupplierDetailsCard.tsx` already calls, never a new fetch shape) and
// price the pick off THAT row instead of the stale in-memory one. The
// server recomputes `priceLevels` for the newly-answered VAT state, so the
// re-fetched row's stored tiers are the correct, VAT-inclusive ones.

import type { MasterCatalogueItem } from '../../lib/masterCatalogueApi';
import { priceAtUnit, type CataloguePriceSelection } from './itemLookupRows';
import { costVatAgreesWithLoadedRate, vatRateChangedError } from './cataloguePickDetails';

export interface PendingCataloguePickVatResult {
  /** The picked row, with `vat_applicable` now the answer. */
  item: MasterCatalogueItem;
  /**
   * The SAME unit (`unitKey`) the popup's own choice named, re-priced for the
   * answered row — or `undefined` when the pick carried no `priceSelection`
   * at all (the row's base-unit cost is applied instead, the same path a
   * pick with no popup choice already took).
   */
  priceSelection: CataloguePriceSelection | undefined;
}

export interface PriceCataloguePickForVatAnswerDeps {
  /**
   * The existing typed single-item fetch (`lib/masterCatalogueApi
   * .getMasterCatalogueItem`) — called on every answer, with or without a
   * `priceSelection`, so no caller ever prices off the pre-answer `priceLevels`.
   */
  getCatalogueItem: (id: string) => Promise<{ success: boolean; data: MasterCatalogueItem }>;
}

/**
 * @throws when the re-fetch comes back with no row, or when a
 *   `priceSelection` was picked and the answered row can no longer be priced
 *   at that SAME unit — e.g. answered "Yes" with no Settings VAT rate loaded
 *   to gross it at. Refuse, never guess (invariant 8): `err.message` is the
 *   same refusal wording `priceAtUnit` already gives the lookup panel for an
 *   unpriceable row.
 */
export async function priceCataloguePickForVatAnswer(
  args: {
    item: MasterCatalogueItem;
    /** Present only when the popup's unit selector was used for this pick. */
    priceSelection?: CataloguePriceSelection;
    vatApplicable: boolean;
    /**
     * The VAT inside the answered row's cost, from the server's answer to the
     * save (`setCatalogueVat`'s `costVatPercent`) — the in-memory `item` is
     * the pre-answer row, whose own fact still says blank. Recorded on the line
     * as `pricingDetails.costVatPercent` (D82); never worked out here.
     */
    costVatPercent: number | null;
    /** TT$ per US$1 — the quotation's own locked rate, or null. Same convention `priceAtUnit` takes. */
    quotationUsdToTtd: number | null;
    /** The Settings VAT rate (`getVatRate`), percent, or null. */
    vatRatePercent: number | null;
  },
  deps: PriceCataloguePickForVatAnswerDeps
): Promise<PendingCataloguePickVatResult> {
  const { item, priceSelection, vatApplicable, costVatPercent, quotationUsdToTtd, vatRatePercent } = args;

  // The fact is the rate the server read answering the PATCH; the cost below
  // is grossed at the rate this page loaded. Refused when they differ
  // (`cataloguePickDetails.costVatAgreesWithLoadedRate`).
  if (!costVatAgreesWithLoadedRate({ vat_applicable: vatApplicable, costVatPercent }, vatRatePercent)) {
    throw vatRateChangedError();
  }

  // FRESH, not the `item` this popup was opened with — see this file's own
  // header, "THE SECOND BUG THIS CLOSED". `setCatalogueVat` has already
  // saved the answer by the time this runs (the page's own
  // `handlePendingCataloguePickAnswer` calls it first), so the server has
  // had the chance to restate the row's stored tiers for it. Re-fetched with
  // or without a unit choice: `applyCataloguePick` can still price a base-unit
  // pick at the asked unit through `priceAtUnit` (its whole-number fallback),
  // which reads the stored tiers (D82 pre-push review).
  const fetched = await deps.getCatalogueItem(item._id);
  if (!fetched?.success || !fetched.data) {
    throw new Error('The Master Catalogue did not return this row after the VAT answer was saved.');
  }
  const answeredItem: MasterCatalogueItem = { ...fetched.data, vat_applicable: vatApplicable, costVatPercent };

  if (!priceSelection) {
    return { item: answeredItem, priceSelection: undefined };
  }

  const result = priceAtUnit(
    { kind: 'catalogue', id: item._id, item: answeredItem },
    { unit: priceSelection.uom, quotationUsdToTtd, vatRatePercent }
  );
  if (!result.ok) {
    throw new Error(result.reason);
  }
  return { item: answeredItem, priceSelection: result.catalogueSelection };
}
